import * as React from 'react';

// ─── Props ────────────────────────────────────────────────────────────────────

interface AnimatedHeightProps {
  children: React.ReactNode;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AnimatedHeight({ children }: AnimatedHeightProps): React.JSX.Element {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const prevHeight = React.useRef<number | null>(null);
  const rafRef = React.useRef<number>(0);
  const [height, setHeight] = React.useState<number | 'auto'>('auto');

  React.useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const observer = new ResizeObserver(() => {
      const container = containerRef.current;
      if (!container) return;

      const to = content.scrollHeight;
      const from = prevHeight.current;
      prevHeight.current = to;

      // Skip animation on first render or same height
      if (from === null || from === to) {
        setHeight('auto');
        return;
      }

      // Lock to previous height, then animate to new
      cancelAnimationFrame(rafRef.current);
      setHeight(from);
      rafRef.current = requestAnimationFrame((): void => {
        setHeight(to);
      });
    });

    observer.observe(content);
    return (): void => {
      observer.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const handleTransitionEnd = (): void => {
    setHeight('auto');
  };

  return (
    <div
      className="transition-[height] duration-300 ease-in-out overflow-hidden"
      onTransitionEnd={handleTransitionEnd}
      ref={containerRef}
      style={{ height: height === 'auto' ? 'auto' : `${String(height)}px` }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
}
